import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/context/AuthContext";
import { useZona } from "@/context/ZonaContext";
import {
  FileSearch, Plus, Search, X, Building2, Euro, TrendingUp, LayoutGrid,
  List, Calendar, Users, ChevronRight, AlertCircle, CheckCircle2,
  Clock, Trophy, ThumbsDown, Archive, Filter, Loader2, Save, BarChart2, Trash2,
  Upload, GitCompare, Layers, AlertTriangle, Link2, ChevronDown, Scale,
} from "lucide-react";
import { toast } from "sonner";
import LicitacionImportModal from "@/components/licitaciones/LicitacionImportModal";
import MunicipiosCompareModal from "@/components/licitaciones/MunicipiosCompareModal";
import CompareLicitacionesModal from "@/components/CompareLicitacionesModal";
import { ConciliacionPresentadasModal } from "@/components/mercado/ConciliacionPresentadasModal";
import DGFilter, { useDGFilter, DG_LABELS } from "@/components/DGFilter";

// ── Config ────────────────────────────────────────────────────────────────────
const ESTADOS = [
  { id: "prospecto",          label: "Prospecto",          color: "bg-gray-100 text-gray-700",      bar: "#9ca3af", ring: "border-gray-300"   },
  { id: "documentacion",      label: "Recogiendo docs",    color: "bg-sky-100 text-sky-700",        bar: "#38bdf8", ring: "border-sky-300"    },
  { id: "analisis",           label: "Analizando",         color: "bg-violet-100 text-violet-700",  bar: "#a78bfa", ring: "border-violet-300" },
  { id: "oferta_preparacion", label: "Prep. oferta",       color: "bg-amber-100 text-amber-700",    bar: "#fbbf24", ring: "border-amber-300"  },
  { id: "presentada",         label: "Presentada",         color: "bg-blue-100 text-blue-700",      bar: "#3b82f6", ring: "border-blue-300"   },
  { id: "negociacion",        label: "Negociación",        color: "bg-orange-100 text-orange-700",  bar: "#f97316", ring: "border-orange-300" },
  { id: "ganada",             label: "GANADA",             color: "bg-emerald-100 text-emerald-800",bar: "#22c55e", ring: "border-emerald-400"},
  { id: "perdida",            label: "Perdida",            color: "bg-red-100 text-red-700",        bar: "#ef4444", ring: "border-red-300"    },
  { id: "descartada",         label: "Descartada",         color: "bg-gray-100 text-gray-500",      bar: "#d1d5db", ring: "border-gray-200"   },
];
const ACTIVE_ESTADOS = ESTADOS.filter(e => !["ganada","perdida","descartada"].includes(e.id));
const TIPOS_SERVICIO = ["RSU / Recogida", "Limpieza Viaria", "Movilidad / ORA", "Zonas Verdes", "Parques Urbanos", "Control Presencia", "Mixto", "Otro"];

function fmtEur(n) {
  if (!n) return "—";
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", notation: "compact", maximumFractionDigits: 1 }).format(n);
}
function fmtEurFull(n) {
  if (!n) return "—";
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

function EstadoBadge({ estado }) {
  const cfg = ESTADOS.find(e => e.id === estado) || ESTADOS[0];
  return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${cfg.color}`}>{cfg.label}</span>;
}

// ── New Licitacion Modal ───────────────────────────────────────────────────────
function NewLicitacionModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    titulo: "", expediente: "", municipio: "", tipo_servicio: "RSU / Recogida",
    estado_crm: "prospecto", probabilidad_exito: 50,
    importe_licitacion: "", fecha_presentacion: "", responsable_comercial: "",
    notas_comerciales: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const INPUT = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, importe_licitacion: form.importe_licitacion ? Number(form.importe_licitacion) : null, probabilidad_exito: Number(form.probabilidad_exito) };
      const res = await axios.post(`${API}/licitaciones`, payload);
      onCreated(res.data);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl my-4" data-testid="new-licitacion-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold font-['Outfit'] flex items-center gap-2">
            <FileSearch size={18} className="text-[#007934]" /> Nuevo Expediente
          </h2>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Título del expediente *</label>
              <input required value={form.titulo} onChange={e => set("titulo", e.target.value)} placeholder="Ej: RSU + Limpieza Viaria San Fernando de Henares" className={INPUT} data-testid="licitacion-titulo-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Nº Expediente</label>
              <input value={form.expediente} onChange={e => set("expediente", e.target.value)} placeholder="2025COMP000141" className={INPUT} data-testid="licitacion-expediente-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Municipio *</label>
              <input required value={form.municipio} onChange={e => set("municipio", e.target.value)} placeholder="San Fernando de Henares" className={INPUT} data-testid="licitacion-municipio-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Tipo de servicio</label>
              <select value={form.tipo_servicio} onChange={e => set("tipo_servicio", e.target.value)} className={INPUT}>
                {TIPOS_SERVICIO.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Estado CRM</label>
              <select value={form.estado_crm} onChange={e => set("estado_crm", e.target.value)} className={INPUT} data-testid="licitacion-estado-select">
                {ESTADOS.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Importe licitación (€)</label>
              <input type="number" value={form.importe_licitacion} onChange={e => set("importe_licitacion", e.target.value)} placeholder="5000000" className={INPUT} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Fecha presentación</label>
              <input type="date" value={form.fecha_presentacion} onChange={e => set("fecha_presentacion", e.target.value)} className={INPUT} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Responsable comercial</label>
              <input value={form.responsable_comercial} onChange={e => set("responsable_comercial", e.target.value)} placeholder="Nombre del comercial" className={INPUT} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Probabilidad de éxito (%)</label>
              <input type="range" min={0} max={100} step={5} value={form.probabilidad_exito} onChange={e => set("probabilidad_exito", e.target.value)} className="w-full" />
              <div className="flex justify-between text-xs text-gray-500 mt-0.5"><span>0%</span><span className="font-bold text-[#007934]">{form.probabilidad_exito}%</span><span>100%</span></div>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Notas comerciales</label>
              <textarea rows={2} value={form.notas_comerciales} onChange={e => set("notas_comerciales", e.target.value)} placeholder="Contexto inicial, fuente del lead, observaciones..." className={`${INPUT} resize-none`} />
            </div>
          </div>
          <div className="flex gap-2 pt-2 border-t">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-200 text-gray-700 rounded-xl py-2.5 text-sm hover:bg-gray-50">Cancelar</button>
            <button type="submit" disabled={saving || !form.titulo || !form.municipio}
              className="flex-1 bg-[#007934] text-white rounded-xl py-2.5 text-sm font-semibold hover:bg-[#006528] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="save-licitacion-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Guardando..." : "Crear expediente"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Licitacion Card ────────────────────────────────────────────────────────────
function LicitacionCard({ lic, onClick, onDelete, onDragStart, draggable }) {
  const daysLeft = lic.fecha_presentacion ? Math.ceil((new Date(lic.fecha_presentacion) - new Date()) / 86400000) : null;
  const hasAnalysis = !!lic.analisis_ia;
  const hasMemoria = !!lic.memoria_tecnica;
  return (
    <div
      onClick={onClick}
      draggable={!!draggable}
      onDragStart={onDragStart}
      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md hover:border-[#007934]/30 transition-all cursor-pointer group relative"
      data-testid={`licitacion-card-${lic.id}`}
    >
      {onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(lic); }}
          className="absolute top-2 right-2 p-1 rounded-md text-gray-300 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all z-10"
          title="Eliminar licitación"
          data-testid={`delete-licitacion-${lic.id}`}
        >
          <Trash2 size={13} />
        </button>
      )}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-400 font-mono truncate">{lic.expediente || "Sin nº"}</p>
          <h3 className="text-sm font-semibold text-gray-900 leading-tight group-hover:text-[#007934] transition-colors">{lic.titulo}</h3>
        </div>
        <EstadoBadge estado={lic.estado_crm} />
      </div>
      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
        <span className="flex items-center gap-1"><Building2 size={11} />{lic.municipio}</span>
        <span className="text-gray-300">·</span>
        <span>{lic.tipo_servicio}</span>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {lic.importe_licitacion ? <span className="flex items-center gap-1 font-medium text-gray-700"><Euro size={11} />{fmtEur(lic.importe_licitacion)}</span> : null}
          {lic.responsable_comercial ? <span className="flex items-center gap-1"><Users size={11} />{lic.responsable_comercial.split(" ")[0]}</span> : null}
          {daysLeft !== null && (
            <span className={`flex items-center gap-1 font-medium ${daysLeft < 0 ? "text-red-500" : daysLeft < 14 ? "text-amber-600" : "text-gray-500"}`}>
              <Calendar size={11} />{daysLeft < 0 ? `Expiró hace ${-daysLeft}d` : `${daysLeft}d`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {hasAnalysis && <span className="w-2 h-2 rounded-full bg-violet-400" title="Analizado con Pando" />}
          {hasMemoria && <span className="w-2 h-2 rounded-full bg-emerald-400" title="Memoria generada" />}
          {(lic.archivos || []).length > 0 && <span className="text-xs text-gray-400">{lic.archivos.length} doc{lic.archivos.length !== 1 ? "s" : ""}</span>}
          <ChevronRight size={14} className="text-gray-300 group-hover:text-[#007934] ml-1" />
        </div>
      </div>
      {lic.probabilidad_exito != null && (
        <div className="mt-2">
          <div className="flex justify-between text-[10px] text-gray-400 mb-0.5">
            <span>Prob. éxito</span><span className="font-medium">{lic.probabilidad_exito}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1">
            <div className="h-1 rounded-full bg-[#007934]" style={{ width: `${lic.probabilidad_exito}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Kanban Column ──────────────────────────────────────────────────────────────
function KanbanColumn({ estado, lics, onCardClick, onDelete, onDropCard, onDragStartCard, dragOverId, setDragOverId }) {
  const cfg = ESTADOS.find(e => e.id === estado.id) || ESTADOS[0];
  const total_imp = lics.reduce((s, l) => s + (l.importe_licitacion || 0), 0);
  const isOver = dragOverId === estado.id;
  return (
    <div
      className="flex-shrink-0 w-64"
      data-testid={`kanban-col-${estado.id}`}
      onDragOver={(e) => { e.preventDefault(); setDragOverId(estado.id); }}
      onDragLeave={() => { if (dragOverId === estado.id) setDragOverId(null); }}
      onDrop={(e) => { e.preventDefault(); setDragOverId(null); onDropCard(estado.id); }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cfg.bar }} />
          <span className="text-xs font-semibold text-gray-700">{cfg.label}</span>
          <span className="text-xs text-gray-400 bg-gray-100 px-1.5 rounded-full">{lics.length}</span>
        </div>
        {total_imp > 0 && <span className="text-[10px] text-gray-400">{fmtEur(total_imp)}</span>}
      </div>
      <div className={`space-y-2 min-h-[80px] rounded-lg transition-colors ${isOver ? "bg-[#007934]/5 ring-2 ring-[#007934]/30 ring-dashed p-1" : ""}`}>
        {lics.map(lic => (
          <LicitacionCard
            key={lic.id}
            lic={lic}
            onClick={() => onCardClick(lic)}
            onDelete={onDelete}
            draggable
            onDragStart={(e) => { onDragStartCard(lic); e.dataTransfer.effectAllowed = "move"; }}
          />
        ))}
        {lics.length === 0 && <div className="border-2 border-dashed border-gray-200 rounded-xl h-16 flex items-center justify-center"><p className="text-xs text-gray-300">{isOver ? "Soltar aquí" : "Sin expedientes"}</p></div>}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Licitaciones() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { zonaGlobal } = useZona();
  const { dg, setDg, filterByDG } = useDGFilter(user?.email);
  const [lics, setLics] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("kanban");
  const [searchQ, setSearchQ] = useState("");
  const [filterEstado, setFilterEstado] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [showCompareLics, setShowCompareLics] = useState(false);
  // Drag & drop state
  const [draggedLic, setDraggedLic] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  // CRM03/C08 · reconciliación con la fuente de autoridad (ofertas_comercial)
  const [recon, setRecon] = useState(null);
  const [reconOpen, setReconOpen] = useState(false);
  const [showConcilPresentadas, setShowConcilPresentadas] = useState(false);

  useEffect(() => {
    axios.get(`${API}/licitaciones/reconciliacion-comercial`)
      .then(r => setRecon(r.data)).catch(() => setRecon(null));
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterEstado) params.estado_crm = filterEstado;
      if (searchQ) params.q = searchQ;
      if (zonaGlobal) params.zona = zonaGlobal;
      const [lRes, sRes] = await Promise.all([
        axios.get(`${API}/licitaciones`, { params }),
        axios.get(`${API}/licitaciones/stats/pipeline`, { params: zonaGlobal ? { zona: zonaGlobal } : {} }),
      ]);
      setLics(lRes.data || []);
      setStats(sRes.data);
    } finally { setLoading(false); }
  }, [filterEstado, searchQ, zonaGlobal]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreated = (lic) => { setShowNew(false); navigate(`/licitaciones/${lic.id}`); };

  // ── Eliminar licitación ──────────────────────────────────────────────────
  const handleDelete = async (lic) => {
    const yes = window.confirm(
      `¿Eliminar definitivamente la licitación "${lic.titulo}"?\n\n` +
      `Se borrarán también sus archivos, análisis Pando y memoria técnica.\n\n` +
      `Esta acción no se puede deshacer.`
    );
    if (!yes) return;
    try {
      await axios.delete(`${API}/licitaciones/${lic.id}`);
      setLics(prev => prev.filter(l => l.id !== lic.id));
      toast.success(`✓ Licitación "${lic.titulo}" eliminada`);
    } catch (e) {
      toast.error("Error eliminando: " + (e.response?.data?.detail || e.message));
    }
  };

  // ── Drag & drop entre columnas Kanban ────────────────────────────────────
  const handleDropOnColumn = async (newEstado) => {
    if (!draggedLic) return;
    const prevEstado = draggedLic.estado_crm;
    const lic = draggedLic;
    setDraggedLic(null);
    if (prevEstado === newEstado) return;

    // Optimistic update
    setLics(prev => prev.map(l => l.id === lic.id ? { ...l, estado_crm: newEstado } : l));
    try {
      await axios.put(`${API}/licitaciones/${lic.id}`, { estado_crm: newEstado });
      const newLabel = (ESTADOS.find(e => e.id === newEstado) || {}).label;
      toast.success(`✓ "${lic.titulo}" → ${newLabel}`);
    } catch (e) {
      // Revertir si falla
      setLics(prev => prev.map(l => l.id === lic.id ? { ...l, estado_crm: prevEstado } : l));
      toast.error("Error moviendo: " + (e.response?.data?.detail || e.message));
    }
  };

  // iter 134 · aplicar el filtro DG global sobre licitaciones y stats
  const licsF = filterByDG(lics);

  // Kanban: group by estado
  const byEstado = ESTADOS.reduce((acc, e) => {
    acc[e.id] = licsF.filter(l => l.estado_crm === e.id);
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="licitaciones-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#007934] flex items-center justify-center">
            <FileSearch size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Análisis de Licitaciones</h1>
            <p className="text-sm text-gray-500">CRM + Workspace Pando para elaboración de ofertas técnicas</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowConcilPresentadas(true)}
            className="flex items-center gap-2 bg-white border border-emerald-300 text-emerald-700 px-3 py-2 rounded-xl text-sm font-semibold hover:bg-emerald-50"
            data-testid="lic-conciliar-presentadas-btn"
            title="Conciliar 'Presentadas' con Mercado: puente de importe y conteo + cómo se calcula">
            <Scale size={14}/> Conciliar con Mercado
          </button>
          <button onClick={() => setShowCompareLics(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white px-3 py-2 rounded-xl text-sm font-bold hover:opacity-90 shadow-sm"
            data-testid="compare-licitaciones-btn"
            title="Comparativa cross-licitación: scoring multi-criterio entre 2-4 sesiones Pando completed">
            <Layers size={14}/> Comparar licitaciones
          </button>
          <button onClick={() => setShowCompare(true)}
            className="flex items-center gap-2 bg-white border border-violet-300 text-violet-700 px-3 py-2 rounded-xl text-sm font-semibold hover:bg-violet-50"
            data-testid="compare-munis-btn"
            title="Comparar municipios indexados (win-rate, ganador típico, competidores)">
            <GitCompare size={14}/> Comparar municipios
          </button>
          {/* Iter 121: "Importar Excel" y "Nuevo expediente" se han movido a MERCADO
              para consolidar todos los imports/creación en un solo sitio. Aquí solo
              se puede editar expedientes existentes clicando sus filas. */}
          <DGFilter value={dg} onChange={setDg} testidPrefix="lic-dg-filter"/>
        </div>
      </div>

      {/* CRM03/C08 (CRM audit 2026-10-13) · Fuente de autoridad. Este kanban es
          legacy; el CRM comercial (Licitaciones comercial / Ofertas) es la
          autoridad. Avisamos de los expedientes que viven en ambos con estado o
          importe divergente, para reconciliarlos en vez de editarlos por duplicado. */}
      {recon && recon.summary.divergentes_total > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50" data-testid="lic-reconciliacion-banner">
          <button type="button" onClick={() => setReconOpen(o => !o)}
            className="w-full flex items-center gap-2 px-4 py-3 text-left" data-testid="lic-reconciliacion-toggle">
            <AlertTriangle size={16} className="text-amber-600 shrink-0" />
            <span className="text-sm text-amber-900">
              <b>{recon.summary.divergentes_total}</b> de {recon.summary.en_ambos} expedientes de este kanban legacy tienen una <b>ficha canónica en Comercial</b> (fuente de autoridad) con estado o importe distinto.
            </span>
            <ChevronDown size={16} className={`ml-auto text-amber-600 transition-transform ${reconOpen ? "rotate-180" : ""}`} />
          </button>
          {reconOpen && (
            <div className="px-4 pb-3 space-y-1.5 max-h-80 overflow-y-auto" data-testid="lic-reconciliacion-list">
              <p className="text-[11px] text-amber-700 mb-1">
                El estado/importe correcto es el de <b>Comercial</b>. No edites estos expedientes aquí: ábrelos en su ficha comercial para reconciliarlos.
              </p>
              {recon.items.filter(i => i.divergente_estado || i.divergente_importe).map(i => (
                <div key={i.legacy_id || i.expediente} className="bg-white border border-amber-200 rounded-lg px-3 py-2 text-xs flex items-center gap-3 flex-wrap" data-testid={`lic-recon-row-${i.legacy_id}`}>
                  <span className="font-mono text-slate-500 shrink-0">{i.expediente}</span>
                  <span className="font-semibold text-slate-800 min-w-[120px] truncate">{i.municipio || i.comercial_cliente || i.titulo}</span>
                  <span className="flex items-center gap-1.5 flex-1 min-w-[260px]">
                    <span className={`px-1.5 py-0.5 rounded ${i.divergente_estado ? "bg-rose-100 text-rose-700" : "bg-gray-100 text-gray-500"}`}>
                      legacy: {i.legacy_estado || "—"}{i.divergente_importe ? ` · ${i.legacy_importe_mm} M€` : ""}
                    </span>
                    <ChevronRight size={12} className="text-slate-300" />
                    <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">
                      comercial: {i.comercial_estado || "—"}{i.comercial_fase ? `/${i.comercial_fase}` : ""}{i.divergente_importe ? ` · ${i.comercial_importe_mm} M€` : ""}
                    </span>
                  </span>
                  <button type="button" onClick={() => navigate(`/ofertas-comercial?tab=list&open=${i.comercial_id}`)}
                    className="shrink-0 inline-flex items-center gap-1 text-[#007934] font-semibold hover:underline" data-testid={`lic-recon-open-${i.legacy_id}`}>
                    <Link2 size={11} /> Abrir en Comercial
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {dg && (
        <div className="text-[11px] text-slate-500" data-testid="lic-dg-active-note">
          Filtro activo: <b className="text-slate-700">{DG_LABELS[dg]}</b> · KPIs y kanban muestran sólo licitaciones de esa DG ({licsF.length} de {lics.length}).
        </div>
      )}

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {(() => {
            // iter 134 · Si hay filtro DG, recomputamos KPIs sobre licsF
            const activas = licsF.filter(l => !["ganada","perdida","descartada"].includes(l.estado_crm)).length;
            const ganadas = licsF.filter(l => l.estado_crm === "ganada").length;
            const pipelineTotal = licsF.reduce((s, l) => s + (Number(l.importe_licitacion) || 0), 0);
            const pipelinePond  = licsF.reduce((s, l) => s + ((Number(l.importe_licitacion) || 0) * (Number(l.probabilidad_exito) || 0) / 100), 0);
            const src = dg ? { activas, ganadas, total_pipeline_eur: pipelineTotal, pipeline_ponderado_eur: pipelinePond } : stats;
            return [
              { label: "Expedientes activos", val: src.activas, icon: FileSearch, cls: "text-blue-600", bg: "bg-blue-50" },
              { label: "Pipeline total", val: fmtEurFull(src.total_pipeline_eur), icon: Euro, cls: "text-amber-600", bg: "bg-amber-50" },
              { label: "Pipeline ponderado", val: fmtEurFull(src.pipeline_ponderado_eur), icon: TrendingUp, cls: "text-[#007934]", bg: "bg-green-50" },
              { label: "Ganadas", val: src.ganadas, icon: Trophy, cls: "text-emerald-600", bg: "bg-emerald-50" },
            ].map(k => (
              <div key={k.label} className={`${k.bg} rounded-xl p-3 border border-white`} data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g,"-")}`}>
                <div className="flex items-center gap-2 mb-1"><k.icon size={15} className={k.cls} /><p className="text-xs text-gray-500">{k.label}</p></div>
                <p className={`text-lg font-bold ${k.cls}`}>{k.val}</p>
              </div>
            ));
          })()}
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Buscar título, expediente, municipio, servicio, responsable, notas..."
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#007934]" data-testid="licitaciones-search" title="Busca en: título, expediente, municipio, tipo de servicio, responsable comercial y notas comerciales" />
          {searchQ && <button onClick={() => setSearchQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"><X size={12} /></button>}
        </div>
        <select value={filterEstado} onChange={e => setFilterEstado(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#007934]" data-testid="filter-estado">
          <option value="">Todos los estados</option>
          {ESTADOS.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
        </select>
        <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg ml-auto">
          <button onClick={() => setViewMode("kanban")} className={`p-1.5 rounded-md transition-all ${viewMode === "kanban" ? "bg-white shadow-sm text-[#007934]" : "text-gray-500"}`} title="Kanban"><LayoutGrid size={15} /></button>
          <button onClick={() => setViewMode("list")} className={`p-1.5 rounded-md transition-all ${viewMode === "list" ? "bg-white shadow-sm text-[#007934]" : "text-gray-500"}`} title="Lista"><List size={15} /></button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400"><Loader2 className="animate-spin mr-2" size={18} />Cargando...</div>
      ) : viewMode === "kanban" ? (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {ESTADOS.filter(e => !["ganada","perdida","descartada"].includes(e.id) || byEstado[e.id]?.length > 0).map(e => (
              <KanbanColumn
                key={e.id}
                estado={e}
                lics={byEstado[e.id] || []}
                onCardClick={lic => navigate(`/licitaciones/${lic.id}`)}
                onDelete={handleDelete}
                onDragStartCard={(lic) => setDraggedLic(lic)}
                onDropCard={handleDropOnColumn}
                dragOverId={dragOverId}
                setDragOverId={setDragOverId}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="licitaciones-table">
            <thead className="bg-[#007934] text-white text-xs">
              <tr>
                {["Expediente", "Municipio", "Tipo", "Estado", "Importe", "Prob.", "Docs", "Pando", "Acciones"].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {licsF.map((lic, i) => (
                <tr key={lic.id} className={`${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"} hover:bg-[#E8F5E9]/50 cursor-pointer transition-colors`} onClick={() => navigate(`/licitaciones/${lic.id}`)} data-testid={`lic-row-${lic.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 text-xs">{lic.titulo}</p>
                    <p className="text-xs text-gray-400 font-mono">{lic.expediente}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{lic.municipio}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{lic.tipo_servicio}</td>
                  <td className="px-4 py-3"><EstadoBadge estado={lic.estado_crm} /></td>
                  <td className="px-4 py-3 text-xs text-gray-700 font-medium">{fmtEur(lic.importe_licitacion)}</td>
                  <td className="px-4 py-3 text-xs font-medium text-[#007934]">{lic.probabilidad_exito}%</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{(lic.archivos || []).length}</td>
                  <td className="px-4 py-3">{lic.analisis_ia ? <CheckCircle2 size={13} className="text-emerald-500" /> : <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={lic.estado_crm}
                        onChange={async (e) => {
                          e.stopPropagation();
                          const newEstado = e.target.value;
                          const prev = lic.estado_crm;
                          setLics(p => p.map(l => l.id === lic.id ? { ...l, estado_crm: newEstado } : l));
                          try {
                            await axios.put(`${API}/licitaciones/${lic.id}`, { estado_crm: newEstado });
                            const nl = (ESTADOS.find(es => es.id === newEstado) || {}).label;
                            toast.success(`✓ → ${nl}`);
                          } catch (err) {
                            setLics(p => p.map(l => l.id === lic.id ? { ...l, estado_crm: prev } : l));
                            toast.error("Error: " + (err.response?.data?.detail || err.message));
                          }
                        }}
                        className="text-[10px] border border-gray-200 rounded px-1.5 py-1 bg-white"
                        data-testid={`estado-select-${lic.id}`}
                      >
                        {ESTADOS.map(es => <option key={es.id} value={es.id}>{es.label}</option>)}
                      </select>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(lic); }}
                        className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                        title="Eliminar"
                        data-testid={`delete-licitacion-row-${lic.id}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {lics.length === 0 && (
                <tr><td colSpan={9} className="text-center py-12 text-gray-400 text-sm">No hay expedientes. Crea el primero con "+ Nuevo expediente"</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showNew && <NewLicitacionModal onClose={() => setShowNew(false)} onCreated={handleCreated} />}
      {showImport && (
        <LicitacionImportModal
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); fetchAll(); }}
        />
      )}
      {showCompare && (
        <MunicipiosCompareModal onClose={() => setShowCompare(false)} />
      )}
      {showConcilPresentadas && (
        <ConciliacionPresentadasModal onClose={() => setShowConcilPresentadas(false)} />
      )}
      {showCompareLics && (
        <CompareLicitacionesModal
          open={showCompareLics}
          onClose={() => setShowCompareLics(false)}
        />
      )}
    </div>
  );
}
